import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useAuthStore, apiErrorMessage } from '@lipaflow/shared'
import { Badge, Button, EmptyState, FilterChips, Modal, PageHeader, formatCurrency, formatDateTime, type BadgeVariant, type FilterChipOption } from '@lipaflow/ui'
import { supportService, type Account, type Conversation, type PaymentHit, type RematchResult } from '../services/support'
import { adminService } from '../services/admin'
import { useSector } from '../sector/sector'

type Tab = 'find' | 'moves' | 'bank' | 'talk' | 'emails'
const TABS: FilterChipOption<Tab>[] = [
  { key: 'find', label: 'Find a payment' },
  { key: 'moves', label: 'Payments to move' },
  { key: 'bank', label: 'Bank messages that failed' },
  { key: 'talk', label: 'Conversations' },
  { key: 'emails', label: "Emails that didn't arrive" },
]
const KIND: Record<string, { label: string; hint: string }> = {
  settlement: { label: 'Reached the bank', hint: 'Money arrived and was recorded.' },
  transaction: { label: 'Payment attempt', hint: 'A payment was started (e.g. an M-PESA prompt). It may not have completed.' },
  proof: { label: 'Sent by the payer', hint: 'A code or M-PESA SMS a payer sent, and what the bank check found.' },
}
const field = 'w-full rounded-button border border-hairline bg-surface px-3 py-2 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const money = (v: string | number | null) => (v == null ? '—' : formatCurrency(Number(v)))

/**
 * Support — answering "I paid and it isn't showing" without a database
 * console. Every tool here is platform-admin only and audited; moving money is
 * a request another admin approves, and matching again is a dry run until
 * you apply it.
 */
export default function SupportPage() {
  const sector = useSector()
  const [tab, setTab] = useState<Tab>('find')
  return (
    <div className="space-y-6">
      <PageHeader
        title="Support"
        subtitle={`Find a payment across ${sector.name} customers, fix where it went, and see why an email didn't arrive.`}
      />
      <FilterChips options={TABS} value={tab} onChange={setTab} />
      {tab === 'find' && <FindPayment />}
      {tab === 'moves' && <Moves />}
      {tab === 'bank' && <BankMessages />}
      {tab === 'talk' && <Conversations />}
      {tab === 'emails' && <Emails />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Find a payment
// ---------------------------------------------------------------------------

function FindPayment() {
  const sector = useSector()
  const [query, setQuery] = useState('')
  const [submitted, setSubmitted] = useState<{ reference?: string; phone?: string } | null>(null)
  const [rematching, setRematching] = useState<PaymentHit | null>(null)
  const [moving, setMoving] = useState<PaymentHit | null>(null)

  const results = useQuery({
    queryKey: ['support', sector.code, 'search', submitted],
    queryFn: () => supportService.searchPayments(sector.code, submitted!),
    enabled: !!submitted,
  })

  const search = (e: React.FormEvent) => {
    e.preventDefault()
    const q = query.trim()
    if (q.length < 3) return
    // A phone number is mostly digits; anything else is a payment reference.
    const digits = q.replace(/[\s+()-]/g, '')
    setSubmitted(/^\d{9,13}$/.test(digits) ? { phone: q } : { reference: q })
  }

  return (
    <div className="space-y-4">
      <form onSubmit={search} className="flex gap-2">
        <input value={query} onChange={(e) => setQuery(e.target.value)} aria-label="M-PESA code, bank reference or phone number"
          placeholder="M-PESA code, bank reference or payer's phone" className={field} />
        <Button type="submit" leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />} disabled={query.trim().length < 3}>Search</Button>
      </form>
      <p className="text-caption text-ink-muted">Searches every {sector.name} customer. Your search is recorded in Platform activity.</p>

      {results.isLoading && <p role="status" className="text-body-sm text-ink-muted">Searching…</p>}
      {results.isError && <p role="alert" className="text-body-sm text-warning">{apiErrorMessage(results.error, "Couldn't search right now.")}</p>}
      {results.data && results.data.length === 0 && (
        <EmptyState title="Nothing found" description="No bank payment, payment attempt or payer report matches. Check the code with the payer." className="py-10" />
      )}
      {results.data && results.data.length > 0 && (
        <ul className="divide-y divide-hairline rounded-card border border-hairline bg-surface">
          {results.data.map((hit) => {
            const unmatched = hit.kind === 'settlement' && hit.detail.matched === false
            const movable = hit.kind === 'settlement' && hit.detail.matched !== false && !hit.detail.reversed
            return (
              <li key={`${hit.kind}-${hit.id}`} className="p-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink">{hit.tenant_name ?? 'Unknown customer'}</span>
                    <Badge variant="neutral" size="sm">{KIND[hit.kind]?.label ?? hit.kind}</Badge>
                    {unmatched && <Badge variant="warning" size="sm">Not matched to an account</Badge>}
                    {hit.detail.reversed && <Badge variant="neutral" size="sm">Reversed</Badge>}
                  </p>
                  <p className="text-body-sm text-ink-muted">
                    <span className="font-mono">{hit.reference ?? 'No reference'}</span>
                    {hit.phone && <> · {hit.phone}</>}
                    {hit.detail.account_reference && <> · paid to account <span className="font-mono">{hit.detail.account_reference}</span></>}
                  </p>
                  <p className="text-caption text-ink-faint">
                    {hit.occurred_at ? formatDateTime(hit.occurred_at) : ''}{hit.status && ` · ${hit.status}`}
                    {hit.detail.failure_reason && ` · ${hit.detail.failure_reason}`}
                  </p>
                </div>
                <div className="text-right space-y-2">
                  <p className="tabular font-medium text-ink">{money(hit.amount)}</p>
                  <div className="flex gap-2 justify-end">
                    {unmatched && <Button size="sm" onClick={() => setRematching(hit)}>Match again</Button>}
                    {movable && <Button size="sm" variant="secondary" onClick={() => setMoving(hit)}>Move to another account</Button>}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {rematching && <RematchModal hit={rematching} onClose={() => setRematching(null)} />}
      {moving && <MoveModal hit={moving} onClose={() => setMoving(null)} />}
    </div>
  )
}

/**
 * The usual reason a payment matches nobody: the student or unit was added
 * after the parent paid. A dry run first shows what would happen.
 */
function RematchModal({ hit, onClose }: { hit: PaymentHit; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [result, setResult] = useState<RematchResult | null>(null)
  const rehearse = useMutation({
    mutationFn: () => supportService.rematch(hit.id, false),
    onSuccess: setResult,
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't try the match")),
  })
  const apply = useMutation({
    mutationFn: () => supportService.rematch(hit.id, true),
    onSuccess: () => {
      toast.success('Matched: the payment is now on that account')
      queryClient.invalidateQueries({ queryKey: ['support', 'search'] })
      onClose()
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't apply the match")),
  })
  return (
    <Modal open onClose={onClose} title="Match this payment again">
      <div className="space-y-4">
        <p className="text-body-sm text-ink">
          {money(hit.amount)} to account <span className="font-mono">{hit.detail.account_reference ?? '—'}</span> at {hit.tenant_name}.
          Usually it matched nobody because the account was added after the payment.
        </p>
        {!result ? (
          <Button onClick={() => rehearse.mutate()} isLoading={rehearse.isPending}>Check what would match</Button>
        ) : result.matched ? (
          <div className="space-y-3">
            <p className="rounded-card bg-success-soft p-3 text-body-sm text-ink">
              It would go to <span className="font-medium">{result.beneficiary_name}</span>. Nothing has changed yet.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={() => apply.mutate()} isLoading={apply.isPending}>Apply the match</Button>
            </div>
          </div>
        ) : (
          <p className="rounded-card bg-warning-soft p-3 text-body-sm text-ink">
            Still no match{result.reason ? `: ${result.reason}` : ''}. Ask the customer to add the account, or move the payment by hand once it's posted.
          </p>
        )}
      </div>
    </Modal>
  )
}

function MoveModal({ hit, onClose }: { hit: PaymentHit; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [q, setQ] = useState('')
  const [target, setTarget] = useState<Account | null>(null)
  const [reason, setReason] = useState('')
  const accounts = useQuery({
    queryKey: ['support', 'accounts', hit.tenant_id, q.trim()],
    queryFn: () => supportService.searchAccounts(hit.tenant_id, q.trim()),
    enabled: !target && q.trim().length >= 2,
  })
  const request = useMutation({
    mutationFn: () => supportService.requestReallocation({ settlement_event_id: hit.id, to_beneficiary_id: target!.id, reason: reason.trim() }),
    onSuccess: () => {
      toast.success('Sent for approval. Another admin must approve the move.')
      queryClient.invalidateQueries({ queryKey: ['support', 'reallocations'] })
      onClose()
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't request the move")),
  })
  return (
    <Modal open onClose={onClose} title="Move a payment to another account">
      <div className="space-y-4">
        <p className="text-body-sm text-ink">{money(hit.amount)} · <span className="font-mono">{hit.reference}</span> at {hit.tenant_name}</p>
        {target ? (
          <div className="flex items-center justify-between rounded-card border border-hairline p-3">
            <span className="text-body-sm text-ink">{target.name} <span className="font-mono text-ink-muted">{target.reference}</span></span>
            <Button size="sm" variant="ghost" onClick={() => setTarget(null)}>Change</Button>
          </div>
        ) : (
          <div>
            <label htmlFor="move-account" className="block text-body-sm font-medium text-ink mb-1">Move it to</label>
            <input id="move-account" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or account number" className={field} />
            <ul className="mt-1">
              {(accounts.data ?? []).map((a) => (
                <li key={a.id}>
                  <button type="button" onClick={() => setTarget(a)} className="w-full rounded-button px-3 py-2 text-left text-body-sm text-ink hover:bg-surface-alt">
                    {a.name} <span className="font-mono text-ink-muted">{a.reference}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <label className="block">
          <span className="block text-body-sm font-medium text-ink mb-1">Why (at least 10 characters)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} className={`${field} h-20 resize-none`}
            placeholder="e.g. Parent paid with a sibling's admission number" />
        </label>
        <p className="text-caption text-ink-muted">Nothing moves until a different admin approves it under "Payments to move".</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => request.mutate()} disabled={!target || reason.trim().length < 10} isLoading={request.isPending}>Request the move</Button>
        </div>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Payments to move (maker-checker)
// ---------------------------------------------------------------------------

const MOVE_STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Waiting for approval', variant: 'warning' },
  approved: { label: 'Moved', variant: 'success' },
  completed: { label: 'Moved', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'neutral' },
}

const account = (a: { reference: string; name: string } | null) =>
  a ? <><span className="font-medium">{a.name}</span> <span className="font-mono text-ink-muted">{a.reference}</span></> : <span className="text-ink-muted">an account</span>

function Moves() {
  const sector = useSector()
  const queryClient = useQueryClient()
  const me = useAuthStore((s) => s.user?.id)
  const [rejecting, setRejecting] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const list = useQuery({
    queryKey: ['support', 'reallocations', sector.code],
    queryFn: () => supportService.reallocations(sector.code),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['support', 'reallocations'] })
  const approve = useMutation({
    mutationFn: (id: string) => supportService.approveReallocation(id),
    onSuccess: () => { toast.success('Payment moved'); refresh() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't move it")),
  })
  const reject = useMutation({
    mutationFn: () => supportService.rejectReallocation(rejecting!, note.trim()),
    onSuccess: () => { toast.success('Rejected'); setRejecting(null); setNote(''); refresh() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't reject it")),
  })

  if (list.isLoading) return <p role="status" className="text-body-sm text-ink-muted">Loading…</p>
  if (!list.data?.length) return <EmptyState title="No payment moves" description='Moves requested from "Find a payment" wait here for a second admin.' className="py-10" />
  return (
    <>
      <ul className="divide-y divide-hairline rounded-card border border-hairline bg-surface">
        {list.data.map((r) => {
          const mine = r.requested_by_user_id === me
          const st = MOVE_STATUS[r.status] ?? { label: r.status, variant: 'neutral' as BadgeVariant }
          return (
            <li key={r.id} className="p-4 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <p className="flex items-center gap-2"><span className="tabular font-medium text-ink">{money(r.amount)}</span><Badge variant={st.variant} size="sm">{st.label}</Badge></p>
                <p className="text-body-sm text-ink">
                  {r.tenant_name ?? 'Customer'}: from {account(r.from_account)} to {account(r.to_account)}
                </p>
                <p className="text-body-sm text-ink-muted"><span className="font-mono">{r.reference ?? '—'}</span> · {r.reason}</p>
                {r.decided_at && <p className="text-caption text-ink-faint">Decided {formatDateTime(r.decided_at)}</p>}
              </div>
              {r.status === 'pending' && (
                mine ? (
                  <p className="text-caption text-ink-muted">You asked for this; another admin must decide.</p>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approve.mutate(r.id)} isLoading={approve.isPending && approve.variables === r.id}>Approve and move</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setRejecting(r.id); setNote('') }}>Reject</Button>
                  </div>
                )
              )}
            </li>
          )
        })}
      </ul>
      <Modal open={!!rejecting} onClose={() => setRejecting(null)} title="Reject this move?" size="sm">
        <div className="space-y-3">
          <label htmlFor="reject-note" className="block text-body-sm font-medium text-ink">Why? The admin who asked will see this.</label>
          <textarea id="reject-note" value={note} onChange={(e) => setNote(e.target.value)} className={`${field} h-20 resize-none`} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setRejecting(null)}>Back</Button>
            <Button variant="secondary" disabled={!note.trim()} isLoading={reject.isPending} onClick={() => reject.mutate()}>Reject</Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

// ---------------------------------------------------------------------------
// Emails that didn't arrive
// ---------------------------------------------------------------------------

function Emails() {
  const sector = useSector()
  const [email, setEmail] = useState('')
  const [lookup, setLookup] = useState<string | null>(null)
  const bounced = useQuery({ queryKey: ['support', 'bounced', sector.code], queryFn: () => supportService.bouncedUsers(sector.code) })
  const events = useQuery({
    queryKey: ['support', 'email-events', sector.code, lookup],
    queryFn: () => supportService.emailEvents(sector.code, lookup!),
    enabled: !!lookup,
  })

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (email.includes('@')) setLookup(email.trim().toLowerCase()) }}>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email address"
            placeholder="Look up an email address" className={field} />
          <Button type="submit" variant="secondary" className="shrink-0 whitespace-nowrap" disabled={!email.includes('@')}>Look up</Button>
        </form>
        {events.data && (events.data.length === 0 ? (
          <p className="text-body-sm text-ink-muted">Nothing recorded for {lookup}: no bounces, complaints or deliveries.</p>
        ) : (
          <ul className="divide-y divide-hairline rounded-card border border-hairline bg-surface">
            {events.data.map((ev) => (
              <li key={ev.id} className="p-3 flex flex-wrap items-center justify-between gap-2 text-body-sm">
                <span className="text-ink">{ev.record_type}{ev.bounce_type && ` · ${ev.bounce_type}`}{ev.description && ` · ${ev.description}`}</span>
                <span className="flex items-center gap-2">
                  {ev.is_hard_failure && <Badge variant="warning" size="sm">Won't be delivered</Badge>}
                  <span className="text-caption text-ink-faint">{formatDateTime(ev.occurred_at)}</span>
                </span>
              </li>
            ))}
          </ul>
        ))}
      </section>

      <section className="space-y-2">
        <h2 className="font-outfit text-h4 text-ink">People missing every email</h2>
        <p className="text-caption text-ink-muted">Their address bounced, so invitations, receipts and reminders don't reach them. Ask the customer for a working address.</p>
        {bounced.isLoading ? (
          <p role="status" className="text-body-sm text-ink-muted">Loading…</p>
        ) : !bounced.data?.length ? (
          <p className="text-body-sm text-ink-muted">Nobody. Every address is receiving email.</p>
        ) : (
          <ul className="divide-y divide-hairline rounded-card border border-hairline bg-surface">
            {bounced.data.map((u) => (
              <li key={u.id} className="p-3 flex flex-wrap items-center justify-between gap-2 text-body-sm">
                <span className="text-ink">{u.email}{u.email_bounce_reason && <span className="text-ink-muted"> · {u.email_bounce_reason}</span>}</span>
                <span className="text-caption text-ink-faint">since {formatDateTime(u.email_bounced_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bank messages that failed
// ---------------------------------------------------------------------------

/** A bank or M-PESA message that failed (or gave up retrying): process it again
 * once the cause is fixed, e.g. the customer's connection now set up. */
function BankMessages() {
  const sector = useSector()
  const queryClient = useQueryClient()
  const failed = useQuery({ queryKey: ['support', 'failed-webhooks', sector.code], queryFn: () => supportService.failedWebhooks(sector.code) })
  const replay = useMutation({
    mutationFn: (id: string) => supportService.replayWebhook(id),
    onSuccess: (r) => {
      if (r.status_after === 'processed') toast.success('Processed: the payment is in')
      else toast.error(`Still ${r.status_after}${r.error ? `: ${r.error}` : ''}`)
      queryClient.invalidateQueries({ queryKey: ['support', 'failed-webhooks'] })
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't process it again")),
  })

  if (failed.isLoading) return <p role="status" className="text-body-sm text-ink-muted">Loading…</p>
  if (!failed.data?.length) {
    return <EmptyState title="Nothing failed" description={`Every bank and M-PESA message for ${sector.name} customers was processed.`} />
  }
  return (
    <section className="space-y-2">
      <p className="text-caption text-ink-muted">
        Processing again is safe: a payment that was already posted isn't posted twice. Each try is in the activity log.
      </p>
      <ul className="divide-y divide-hairline rounded-card border border-hairline bg-surface">
        {failed.data.map((w) => (
          <li key={w.id} className="p-3 flex flex-wrap items-start justify-between gap-2 text-body-sm">
            <div>
              <p className="text-ink">{w.tenant_name ?? 'Unknown customer'} · {w.provider.toUpperCase()} {w.event_type.replace(/_/g, ' ')}</p>
              <p className="text-caption text-ink-muted">
                {w.error_message ?? 'No reason recorded'}{w.received_at ? ` · received ${formatDateTime(w.received_at)}` : ''}
                {w.retry_count ? ` · tried ${w.retry_count} times` : ''}
              </p>
            </div>
            <span className="flex items-center gap-2">
              <Badge variant={w.processing_status === 'dlq' ? 'warning' : 'neutral'} size="sm">
                {w.processing_status === 'dlq' ? 'Gave up retrying' : 'Failed'}
              </Badge>
              <Button size="sm" variant="secondary" isLoading={replay.isPending && replay.variables === w.id}
                disabled={replay.isPending} onClick={() => replay.mutate(w.id)}>Process again</Button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

/** A customer's conversations with their payers; opening one is audited. */
function Conversations() {
  const sector = useSector()
  const [tenantId, setTenantId] = useState('')
  const [open, setOpen] = useState<Conversation | null>(null)
  const customers = useQuery({ queryKey: ['tenants', sector.code], queryFn: () => adminService.listTenants(sector.code) })
  const list = useQuery({
    queryKey: ['support', 'conversations', sector.code, tenantId],
    queryFn: () => supportService.conversations(sector.code, tenantId),
    enabled: !!tenantId,
  })

  return (
    <section className="space-y-3">
      <label className="block max-w-md">
        <span className="block text-caption text-ink-muted mb-1">Customer</span>
        <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className={field} aria-label="Customer">
          <option value="">{customers.isLoading ? 'Loading…' : 'Choose…'}</option>
          {(customers.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </label>
      {tenantId && list.isLoading && <p role="status" className="text-body-sm text-ink-muted">Loading…</p>}
      {tenantId && list.data && (list.data.length === 0 ? (
        <p className="text-body-sm text-ink-muted">No conversations with their payers yet.</p>
      ) : (
        <ul className="divide-y divide-hairline rounded-card border border-hairline bg-surface">
          {list.data.map((c) => (
            <li key={c.id} className="p-3 flex flex-wrap items-center justify-between gap-2 text-body-sm">
              <span className="text-ink">
                {c.client_name ?? c.client_phone ?? 'Unknown payer'}
                <span className="text-ink-muted"> · {c.messages} {c.messages === 1 ? 'message' : 'messages'}
                  {c.last_message_at ? ` · last ${formatDateTime(c.last_message_at)}` : ''}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setOpen(c)}>Read</Button>
            </li>
          ))}
        </ul>
      ))}
      {open && <TranscriptModal conversation={open} onClose={() => setOpen(null)} />}
    </section>
  )
}

function TranscriptModal({ conversation, onClose }: { conversation: Conversation; onClose: () => void }) {
  const transcript = useQuery({
    queryKey: ['support', 'transcript', conversation.id],
    queryFn: () => supportService.transcript(conversation.id),
    staleTime: Infinity,
  })
  return (
    <Modal open onClose={onClose} title={conversation.client_name ?? conversation.client_phone ?? 'Conversation'} size="lg">
      <p className="text-caption text-ink-muted mb-3">Read-only. Opening it is recorded in the activity log.</p>
      {transcript.isLoading && <p role="status" className="text-body-sm text-ink-muted">Loading…</p>}
      {transcript.isError && <p role="alert" className="text-body-sm text-warning">{apiErrorMessage(transcript.error, "Couldn't load the conversation.")}</p>}
      <ol className="space-y-2">
        {(transcript.data?.messages ?? []).map((m) => (
          <li key={m.id} className={m.direction === 'inbound' ? 'mr-12' : 'ml-12'}>
            <div className={`rounded-button px-3 py-2 text-body-sm ${m.direction === 'inbound' ? 'bg-surface-alt text-ink' : 'bg-accent/10 text-ink'}`}>
              {m.content ?? (m.template ? `Template: ${m.template}` : `(${m.type})`)}
            </div>
            <p className={`text-caption text-ink-faint ${m.direction === 'inbound' ? '' : 'text-right'}`}>
              {m.direction === 'inbound' ? 'Payer' : 'Sent'} · {formatDateTime(m.at)}
            </p>
          </li>
        ))}
      </ol>
    </Modal>
  )
}
